import type {
  NormSetPeer,
  PeerEntry,
  ProductLookupRow,
} from '@graphql/queries/PerceptionModelQueries';

/**
 * Ranking for the norm set picker.
 *
 * Each anchor's lookup row already carries its ~200 nearest peers, so the whole
 * ranking is a merge over a few hundred entries. That is cheap enough to do in
 * the browser and saves a DB function plus a round trip.
 */

export type Candidate = {
  product: string;
  /**
   * Mean distance across ALL anchors, with each anchor's truncation bound
   * substituted where it does not list the product. See rankCandidates.
   */
  meanDist: number;
  /** closest single anchor */
  minDist: number;
  /** how many anchors actually list it — anchorHits < anchor count means censored */
  anchorHits: number;
  /** true when at least one anchor did not list this product */
  censored: boolean;
  rank: number;
};

export const DEFAULT_TOP_N = 100;
export const MIN_PEERS = 30;

export const round4 = (n: number): number => Math.round(n * 1e4) / 1e4;

/**
 * Merge the anchors' peer lists into one ranked candidate list.
 *
 * The anchors themselves are not listed. Not because a product may not belong
 * to a set it anchors — it may, a norm set is a heuristic for choosing products
 * rather than a partition — but because this table exists to surface products
 * the analyst has *not* chosen yet, and every row in it is included by default.
 * An anchor is already on screen as an anchor; ranking it at 0 would put it at
 * the top of a list of discoveries and quietly enrol it in every set built from
 * then on. Adding one is a deliberate act, done from the map, and nothing
 * refuses it: see `distanceToAnchors`.
 *
 * Peer lists are truncated at k, so a product missing from an anchor's list is
 * not "unknown" — it is known to be at least as far as that anchor's furthest
 * listed peer. Averaging only over the anchors that happen to list a product
 * would divide by different denominators per row and bias the mean *downward*
 * for products only one anchor knows, floating them to the top of a ranking
 * where every row is included by default.
 *
 * So each anchor contributes its own truncation bound where it has no entry.
 * Every candidate is then averaged over the same set of anchors, which makes
 * the numbers comparable, and because the bound is a lower bound on the true
 * distance the estimate stays conservative. `censored` marks the rows this
 * substitution touched.
 */
export const rankCandidates = (
  anchorRows: ProductLookupRow[],
  topN: number = DEFAULT_TOP_N,
): Candidate[] => {
  const anchorNames = new Set(anchorRows.map((r) => r.productName));

  // Per anchor: its usable peer entries, plus the bound for everything it omits.
  const perAnchor = anchorRows.map((row) => {
    const dists = new Map<string, number>();
    let bound = 0;
    (row.peers ?? []).forEach((entry: PeerEntry) => {
      const [product, dist] = entry ?? ([] as unknown as PeerEntry);
      // Guard the JSONB: a malformed entry must not poison the average.
      if (typeof product !== 'string' || !Number.isFinite(dist)) return;
      dists.set(product, dist);
      if (dist > bound) bound = dist;
    });
    return { dists, bound };
  });

  const contributing = perAnchor.filter((a) => a.dists.size > 0);
  if (contributing.length === 0) return [];

  // Anchors are dropped here rather than during the scan, so their entries
  // still inform the bound and remain available to `distanceToAnchors` — this
  // is only about what the table offers.
  const products = new Set<string>();
  contributing.forEach((a) =>
    a.dists.forEach((_, p) => {
      if (!anchorNames.has(p)) products.add(p);
    }),
  );

  return Array.from(products)
    .map((product) => {
      let sum = 0;
      let min = Infinity;
      let hits = 0;
      contributing.forEach((a) => {
        const d = a.dists.get(product);
        if (d === undefined) {
          sum += a.bound; // censored: at least this far
        } else {
          sum += d;
          hits += 1;
          if (d < min) min = d;
        }
      });
      return {
        product,
        meanDist: sum / contributing.length,
        minDist: min,
        anchorHits: hits,
        censored: hits < contributing.length,
      };
    })
    .sort((a, b) =>
      a.meanDist !== b.meanDist
        ? a.meanDist - b.meanDist
        : a.product.localeCompare(b.product),
    )
    .slice(0, topN)
    .map((c, i) => ({ ...c, rank: i + 1 }));
};

/**
 * The members of a saved norm set, ready to display. Guards the jsonb: a
 * malformed or duplicated entry must not become a phantom member. Nearest
 * first, unknown distances last.
 */
export const normSetMembers = (saved: NormSetPeer[] | null): NormSetPeer[] => {
  const seen = new Set<string>();
  const members: NormSetPeer[] = [];

  (saved ?? []).forEach((peer) => {
    if (!peer || typeof peer.product !== 'string') return;
    if (seen.has(peer.product)) return;
    seen.add(peer.product);
    members.push({
      product: peer.product,
      mean_dist: Number.isFinite(peer.mean_dist) ? peer.mean_dist : null,
    });
  });

  return members.sort((a, b) => {
    const av = a.mean_dist ?? Infinity;
    const bv = b.mean_dist ?? Infinity;
    return av !== bv ? av - bv : a.product.localeCompare(b.product);
  });
};

export type Gates = {
  hasAnchors: boolean;
  anchorsResolved: boolean;
  enoughPeers: boolean;
  passes: boolean;
};

/**
 * What must hold before a norm set can be saved. `missingAnchors` are products
 * the analyst picked that have no lookup row — not in the model, so they cannot
 * anchor anything.
 */
export const evaluateGates = (
  anchorRows: ProductLookupRow[],
  missingAnchors: string[],
  includedCount: number,
  minPeers: number = MIN_PEERS,
): Gates => {
  const hasAnchors = anchorRows.length > 0;
  const anchorsResolved = missingAnchors.length === 0;
  const enoughPeers = includedCount >= minPeers;
  return {
    hasAnchors,
    anchorsResolved,
    enoughPeers,
    passes: hasAnchors && enoughPeers,
  };
};

/**
 * Mean ggvar intensity across the anchors, used as the reference row the
 * candidate strips are read against.
 */
export const anchorGgvarMean = (
  anchorRows: ProductLookupRow[],
): Record<string, number> => {
  const sums = new Map<string, { sum: number; n: number }>();
  anchorRows.forEach((row) => {
    Object.entries(row.ggvars ?? {}).forEach(([k, v]) => {
      const val = v?.intensity;
      if (!Number.isFinite(val)) return;
      const prev = sums.get(k);
      if (prev) {
        prev.sum += val as number;
        prev.n += 1;
      } else {
        sums.set(k, { sum: val as number, n: 1 });
      }
    });
  });
  const out: Record<string, number> = {};
  sums.forEach((v, k) => {
    out[k] = v.sum / v.n;
  });
  return out;
};

/** Top-n tokens by prevalence, for the chips in the detail panel. */
export const topTokens = (
  tokens: Record<string, number> | null,
  n = 8,
): Array<[string, number]> =>
  Object.entries(tokens ?? {})
    .filter(([, v]) => Number.isFinite(v))
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);

/**
 * Anchors whose model row was not built from a product this workspace owns.
 * * Product names are not unique (~1.6k rows share a name with another product)
 * and the model universe is keyed on name, so an analyst can pick what looks
 * like their product and match a row derived from a different producer's.
 * Each lookup row carries the product ids behind it; if none of them belong to
 * the workspace, the match is suspect.
 *
 * Only reports a mismatch when both sides are known — absent product_ids means
 * "cannot tell", not "wrong".
 */
export const findAmbiguousAnchors = (
  anchorRows: ProductLookupRow[],
  workspaceProducts: Array<{ id: number; name: string }>,
): string[] => {
  if (workspaceProducts.length === 0) return [];

  const idsByName = new Map<string, Set<number>>();
  workspaceProducts.forEach((p) => {
    const set = idsByName.get(p.name) ?? new Set<number>();
    set.add(p.id);
    idsByName.set(p.name, set);
  });

  return anchorRows
    .filter((row) => {
      const mine = idsByName.get(row.productName);
      const modelled = row.productIds;
      if (!mine || !Array.isArray(modelled) || modelled.length === 0) {
        return false;
      }
      return !modelled.some((id) => mine.has(id));
    })
    .map((row) => row.productName);
};

/** The gastrograph.product ids the model was built from, for one job. */
export const modelledProductIds = (
  rows: Array<Pick<ProductLookupRow, 'productIds'>>,
): Set<number> => {
  const ids = new Set<number>();
  rows.forEach((row) => {
    if (!Array.isArray(row?.productIds)) return;
    row.productIds.forEach((id) => {
      if (Number.isInteger(id)) ids.add(id);
    });
  });
  return ids;
};

/**
 * The products this workspace can actually anchor on.
 *
 * Only products the model was built from can anchor a norm set — everything
 * else has no peer list to rank against. Offering the whole workspace and
 * reporting the mismatch after the fact is a poor trade: one workspace here has
 * 7,988 products and 17 modelled ones, so almost every pick would be wrong.
 *
 * The join is on product id via the lookup's product_ids, not on name. Names
 * are not unique across producers, so a name join would offer a product whose
 * model row was actually built from someone else's product of the same name.
 */
export const anchorOptions = (
  products: Array<{ id: number; name: string }>,
  modelled: Set<number>,
): string[] =>
  Array.from(
    new Set(products.filter((p) => modelled.has(p.id)).map((p) => p.name)),
  ).sort();

export type PeerBearing = Pick<ProductLookupRow, 'productName' | 'peers'>;

/**
 * Mean distance from the anchors to a product the ranking never surfaced.
 *
 * A custom candidate is found by token search, so it is usually outside the
 * anchors' truncated peer lists and `rankCandidates` cannot score it. Writing
 * it with no distance would leave a member nothing can compare against, so the
 * same estimate the ranking would have produced is computed directly.
 *
 * An anchor is scored like anything else. It is at distance 0 from itself and
 * at its measured distance from the others, so a product that is both a
 * reference and a subject gets a real number rather than being refused.
 *
 * This deliberately mirrors `rankCandidates` exactly — forward lookup, the
 * anchor's own truncation bound substituted where it does not list the
 * product, averaged over every contributing anchor. It does **not** consult the
 * product's own peer list, even though the stored metric is symmetric and that
 * would often recover the true distance. Doing so would make the same product
 * score differently depending on whether the top-N slider happened to rank it,
 * and both paths write the same `norm_set.mean_dist` column. A number that
 * changes with a UI control is worse than a number that is uniformly a
 * conservative lower bound.
 */
export const distanceToAnchors = (
  anchorRows: PeerBearing[],
  productName: string,
): { meanDist: number; anchorHits: number; censored: boolean } | null => {
  const scored = anchorRows
    .map((anchor) => {
      let bound = 0;
      let forward: number | undefined;
      let usable = 0;

      (anchor.peers ?? []).forEach((entry: PeerEntry) => {
        const [product, dist] = entry ?? ([] as unknown as PeerEntry);
        if (typeof product !== 'string' || !Number.isFinite(dist)) return;
        usable += 1;
        if (dist > bound) bound = dist;
        if (product === productName) forward = dist;
      });

      if (usable === 0) return null;
      // A product is exactly itself: a measurement, not a truncation bound, so
      // it is not censored either.
      if (anchor.productName === productName) return { dist: 0, hit: true };
      return forward === undefined
        ? { dist: bound, hit: false }
        : { dist: forward, hit: true };
    })
    .filter((x): x is { dist: number; hit: boolean } => x !== null);

  if (scored.length === 0) return null;

  const hits = scored.filter((s) => s.hit).length;

  return {
    meanDist: scored.reduce((acc, s) => acc + s.dist, 0) / scored.length,
    anchorHits: hits,
    censored: hits < scored.length,
  };
};

/**
 * The norm set to store: every included product with its distance to the
 * anchors.
 *
 * A ranked product already carries one. A custom candidate never entered the
 * ranking, so its distance is recomputed the way the ranking would have — both
 * end up in the same column, so they have to agree.
 *
 * One entry per member by construction, so the stored set cannot silently
 * disagree with what the analyst ticked.
 */
export const buildNormSet = (
  candidates: Candidate[],
  included: Set<string>,
  anchorRows: PeerBearing[],
): NormSetPeer[] => {
  const ranked = new Map(candidates.map((c) => [c.product, c.meanDist]));

  return Array.from(included)
    .map((product) => {
      const dist =
        ranked.get(product) ?? distanceToAnchors(anchorRows, product)?.meanDist;
      return {
        product,
        mean_dist: dist === undefined ? null : round4(dist),
      };
    })
    .sort((a, b) => {
      // Unknown distances sort last rather than colliding with 0.
      const av = a.mean_dist ?? Infinity;
      const bv = b.mean_dist ?? Infinity;
      return av !== bv ? av - bv : a.product.localeCompare(b.product);
    });
};

/** What a proposed norm set looks like, before anyone edits it. */
export type NormSetStats = {
  count: number;
  nearest: number | null;
  farthest: number | null;
  median: number | null;
  /** members at least one anchor never listed, so their distance is a bound */
  censored: number;
};

/**
 * Summary of the members actually included.
 *
 * Median rather than mean: the ranking is a long tail by construction — the
 * farthest few sit at their anchors' truncation bounds — and a mean would be
 * dragged by exactly the members that are least precisely known.
 */
export const normSetStats = (
  candidates: Candidate[],
  included: Set<string>,
): NormSetStats => {
  const members = candidates.filter((c) => included.has(c.product));
  const dists = members
    .map((c) => c.meanDist)
    .filter((d) => Number.isFinite(d))
    .sort((a, b) => a - b);

  const mid = Math.floor(dists.length / 2);
  const median = dists.length
    ? dists.length % 2
      ? dists[mid]
      : (dists[mid - 1] + dists[mid]) / 2
    : null;

  return {
    // included can hold custom picks the ranking never produced, so it is the
    // count of the set rather than of the rows above.
    count: included.size,
    nearest: dists.length ? dists[0] : null,
    farthest: dists.length ? dists[dists.length - 1] : null,
    median,
    censored: members.filter((c) => c.censored).length,
  };
};

/**
 * What moving the anchors would do to a saved norm set.
 *
 * Membership is frozen, so this keeps exactly the products already in the set
 * and only re-measures them. Every distance in the row was taken *from* the
 * anchors, so changing the anchors without recomputing all of them would leave
 * a set of numbers describing a comparison that was never made — which is why
 * this returns both halves and the mutation writes them together.
 *
 * A member that is also an anchor is measured, not refused: it is 0 from itself
 * and its real distance from the others. A norm set is a heuristic for choosing
 * products, so a product being both a reference and a subject is a legitimate
 * thing to want and not a state to be rescued from.
 *
 * The one case worth stopping on is reported rather than silently absorbed:
 *
 * - **warned** — the product sits outside some anchor's peer list, so its
 *   distance is that anchor's truncation bound rather than a measurement. Still
 *   a number, but an upper limit about to be stored as fact.
 */
export type Remeasure = {
  /** the new row, same members, distances recomputed */
  normSet: NormSetPeer[];
  /** members whose distance would be a bound rather than a measurement */
  warned: string[];
  /** members no proposed anchor can place at all */
  unmeasured: string[];
};

export const remeasureNormSet = (
  members: string[],
  anchorRows: PeerBearing[],
): Remeasure => {
  const normSet: NormSetPeer[] = [];
  const warned: string[] = [];
  const unmeasured: string[] = [];

  members.forEach((product) => {
    const scored = distanceToAnchors(anchorRows, product);

    if (!scored) {
      unmeasured.push(product);
      normSet.push({ product, mean_dist: null });
      return;
    }

    if (scored.censored) warned.push(product);
    normSet.push({ product, mean_dist: round4(scored.meanDist) });
  });

  normSet.sort((a, b) => {
    // Unknown distances sort last rather than colliding with 0.
    const av = a.mean_dist ?? Infinity;
    const bv = b.mean_dist ?? Infinity;
    return av !== bv ? av - bv : a.product.localeCompare(b.product);
  });

  return { normSet, warned, unmeasured };
};
